import React, { useState, useEffect, useRef } from 'react';
import { aiChat } from '../services/api';
import { useTranslation } from 'react-i18next';

interface Message {
  id: string;
  content: string;
  reasoning?: string;
  sender: 'user' | 'ai';
  timestamp: Date;
  isStreaming?: boolean;
  showReasoning?: boolean;
}

interface AIChatProps {
  patientId: string;
  patient: any;
}

const AIChat: React.FC<AIChatProps> = ({ patientId, patient }) => {
  const { t } = useTranslation();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [streamingMessageId, setStreamingMessageId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const closeStreamRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    // Load chat history
    const fetchChatHistory = async () => {
      try {
        setLoading(true);
        const history = await aiChat.getHistory(patientId);
        
        // Convert API response history to component format
        const formattedMessages = history.map((msg: any) => ({
          id: msg.message_id || msg.id,
          content: msg.content,
          reasoning: msg.reasoning_content,
          sender: msg.sender,
          timestamp: new Date(msg.created_at || msg.timestamp)
        }));
        
        if (formattedMessages.length === 0) {
          // If no history, add a welcome message
          setMessages([{
            id: '1',
            content: t('aiChat.welcomeMessage', { name: patient.name }),
            reasoning: '',
            sender: 'ai',
            timestamp: new Date()
          }]);
        } else {
          setMessages(formattedMessages);
        }
      } catch (error) {
        console.error(t('aiChat.historyError'), error);
        // If getting history fails, display a welcome message
        setMessages([{
          id: '1',
          content: t('aiChat.welcomeMessage', { name: patient.name }),
          reasoning: '',
          sender: 'ai',
          timestamp: new Date()
        }]);
      } finally {
        setLoading(false);
      }
    };

    if (patientId) {
      fetchChatHistory();
    }

    // Close stream connection when component unmounts
    return () => {
      if (closeStreamRef.current) {
        closeStreamRef.current();
      }
    };
  }, [patientId, patient.name, t]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async () => {
    if (!input.trim() || loading) return;

    // Create user message
    const userMessage: Message = {
      id: Date.now().toString(),
      content: input,
      reasoning: '',
      sender: 'user',
      timestamp: new Date(),
    };

    // Create initial AI message (empty content, will be updated via stream)
    const initialAiMessage: Message = {
      id: 'temp-' + Date.now().toString(),
      content: '',
      reasoning: '',
      sender: 'ai',
      timestamp: new Date(),
      isStreaming: true,
      showReasoning: false
    };

    // Add messages to UI
    setMessages((prev) => [...prev, userMessage, initialAiMessage]);
    setInput('');
    setLoading(true);
    setStreamingMessageId(initialAiMessage.id);

    // Determine if this is a research-related question
    const isResearchMode = input.toLowerCase().includes('research') || 
                          input.toLowerCase().includes('literature') || 
                          input.toLowerCase().includes('study') ||
                          input.toLowerCase().includes('statistics') ||
                          input.toLowerCase().includes('paper') ||
                          input.toLowerCase().includes('publication');
    
    // Set mode based on the content
    const chatMode = isResearchMode ? 'research' : 'medical';

    const closeStream = aiChat.sendMessageStream(
      patientId,
      input,
      // Process incoming chunks
      (chunk) => {
        setMessages((prevMessages) => {
          // Find the streaming message
          const updatedMessages = [...prevMessages];
          const streamingMessageIndex = updatedMessages.findIndex(
            (msg) => msg.id === initialAiMessage.id
          );
          
          if (streamingMessageIndex !== -1) {
            // Update content with new chunk
            updatedMessages[streamingMessageIndex] = {
              ...updatedMessages[streamingMessageIndex],
              content: updatedMessages[streamingMessageIndex].content + (chunk.content || ''),
              reasoning: updatedMessages[streamingMessageIndex].reasoning + (chunk.reasoning_content || '')
            };
          }
          
          return updatedMessages;
        });
      },
      // Stream transmission completed
      (messageId) => {
        setMessages((prevMessages) => {
          // Find the streaming message
          const updatedMessages = [...prevMessages];
          const streamingMessageIndex = updatedMessages.findIndex(
            (msg) => msg.id === initialAiMessage.id
          );
          
          if (streamingMessageIndex !== -1) {
            // Update messageID and remove streaming flag
            updatedMessages[streamingMessageIndex] = {
              ...updatedMessages[streamingMessageIndex],
              id: messageId,
              isStreaming: false
            };
          }
          
          return updatedMessages;
        });
        
        setLoading(false);
        setStreamingMessageId(null);
        closeStreamRef.current = null;
      },
      // Handle errors
      (error) => {
        console.error(t('aiChat.streamError'), error);
        
        setMessages((prevMessages) => {
          // Find the streaming message
          const updatedMessages = [...prevMessages];
          const streamingMessageIndex = updatedMessages.findIndex(
            (msg) => msg.id === initialAiMessage.id
          );
          
          if (streamingMessageIndex !== -1) {
            // Update to error message
            updatedMessages[streamingMessageIndex] = {
              ...updatedMessages[streamingMessageIndex],
              content: t('aiChat.errorMessage'),
              isStreaming: false
            };
          }
          
          return updatedMessages;
        });
        
        setLoading(false);
        setStreamingMessageId(null);
        closeStreamRef.current = null;
      },
      // Pass the mode parameter
      chatMode
    );
    
    closeStreamRef.current = closeStream;
  };

  // Provide some example questions for user to click
  const exampleQuestions = [
    t('aiChat.exampleQuestions.diagnostic'),
    t('aiChat.exampleQuestions.diagnosis'),
    t('aiChat.exampleQuestions.treatment'),
    t('aiChat.exampleQuestions.tests'),
  ];

  const handleExampleClick = (question: string) => {
    setInput(question);
  };

  // Cancel current streaming transmission
  const handleCancelStream = () => {
    if (closeStreamRef.current) {
      closeStreamRef.current();
      closeStreamRef.current = null;
    }
    
    setMessages((prevMessages) => {
      if (!streamingMessageId) return prevMessages;
      
      // Find the streaming message
      const updatedMessages = [...prevMessages];
      const streamingMessageIndex = updatedMessages.findIndex(
        (msg) => msg.id === streamingMessageId
      );
      
      if (streamingMessageIndex !== -1) {
        // Add cancellation mark
        updatedMessages[streamingMessageIndex] = {
          ...updatedMessages[streamingMessageIndex],
          content: updatedMessages[streamingMessageIndex].content + "\n\n" + t('aiChat.generationCancelled'),
          isStreaming: false
        };
      }
      
      return updatedMessages;
    });
    
    setLoading(false);
    setStreamingMessageId(null);
  };

  return (
    <div className="flex flex-col h-[calc(100vh-158px)] lg:h-[calc(100vh-100px)] overflow-hidden relative">
      {/* Chat messages area - scrollable with padding at bottom to prevent content from being hidden behind input */}
      <div className="absolute inset-0 overflow-y-auto pb-[calc(80px+1rem)]">
        <div className="p-4 space-y-4">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-xs md:max-w-md lg:max-w-lg xl:max-w-xl p-3 rounded-lg ${
                  message.sender === 'user'
                    ? 'bg-indigo-100 text-gray-800'
                    : 'bg-white border border-gray-200 text-gray-800'
                }`}
              >
                {message.sender === 'ai' && message.reasoning && (
                  <>
                    <button 
                      onClick={(e) => {
                        // Prevent default behavior
                        e.preventDefault();
                        // Store current scroll position
                        const scrollPosition = window.scrollY;
                        
                        setMessages(prevMessages => 
                          prevMessages.map(msg => 
                            msg.id === message.id 
                              ? {...msg, showReasoning: !msg.showReasoning} 
                              : msg
                          )
                        );
                        
                        // Use setTimeout to restore scroll position after re-render
                        setTimeout(() => {
                          window.scrollTo({
                            top: scrollPosition,
                            behavior: 'auto'
                          });
                        }, 0);
                      }}
                      className="text-xs text-indigo-600 mb-2 hover:underline"
                    >
                      {message.showReasoning ? t('aiChat.hideReasoning') : t('aiChat.showReasoning')}
                    </button>
                    
                    {message.showReasoning && (
                      <div className="mb-3 p-2 bg-gray-50 rounded border border-gray-200">
                        <p className="text-xs text-gray-700 whitespace-pre-line">{message.reasoning}</p>
                      </div>
                    )}
                  </>
                )}

                <p className="text-sm whitespace-pre-line">{message.content}</p>
                
                <div className="flex justify-between items-center mt-1">
                  <p className="text-xs text-gray-500">
                    {message.timestamp.toLocaleTimeString('zh-CN')}
                  </p>
                  {message.isStreaming && (
                    <div className="flex space-x-1 ml-2">
                      <div className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-bounce"></div>
                      <div className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-bounce delay-75"></div>
                      <div className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-bounce delay-150"></div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Fixed bottom section */}
      <div className="absolute bottom-0 left-0 right-0 bg-white shadow-md">
        {/* Example questions - full width and borderless */}
        {messages.length <= 4 && (
          <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 w-full">
            <div className="flex flex-wrap gap-2 w-full">
              {exampleQuestions.map((question, index) => (
                <button
                  key={index}
                  onClick={() => handleExampleClick(question)}
                  className="text-xs bg-white rounded-full px-3 py-1 hover:bg-gray-100"
                >
                  {question}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input box - absolutely positioned at bottom with smaller margin */}
        <div className=" bg-white p-3">
          <div className="flex space-x-2 max-w-7xl mx-auto px-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
              placeholder={t('aiChat.inputPlaceholder')}
              className="flex-1 border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              disabled={loading}
            />
            {loading && streamingMessageId ? (
              <button
                onClick={handleCancelStream}
                className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                {t('aiChat.stop')}
              </button>
            ) : (
              <button
                onClick={handleSendMessage}
                disabled={loading || !input.trim()}
                className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
              >
                {t('aiChat.send')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIChat; 